import React, { useEffect, useMemo, useState } from 'react';
import "./mcProfileGate.css";
import { signOut } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { getAuthClient } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import {
  buildProfileDisplayName,
  clearCurrentProfile,
  ensureAdminProfile,
  formatProfileAllowedSites,
  getCurrentProfile,
  isAdminProfile,
  setCurrentProfile,
  setMcUserContext,
} from '../utils/profileAccess';
import { isMcCloudSyncReady, getMcCloudSyncState, forceMcCloudSync, MC_SYNC_READY_EVENT } from '../utils/mcCloudSync';
import { recordActivity } from '../utils/mcActivityEvents';

const MC_ACCESS_UNLOCKED_KEY = 'mc-access-layer-unlocked';

function getStoredMcTheme() {
  if (typeof window === 'undefined') return 'dark';
  try { return window.localStorage.getItem('mc-theme') || 'dark'; } catch (_) { return 'dark'; }
}

function isAccessUnlockedForThisTab() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(MC_ACCESS_UNLOCKED_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

function setAccessUnlockedForThisTab(value) {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.sessionStorage.setItem(MC_ACCESS_UNLOCKED_KEY, 'true');
    else window.sessionStorage.removeItem(MC_ACCESS_UNLOCKED_KEY);
  } catch (_) {
    // Storage can be full or blocked. The active profile itself is still the
    // source of truth, so never crash or force the Access Layer because this
    // tab marker could not be written.
  }
}

function isSafeMcSyncReady(state = getMcCloudSyncState()) {
  return Boolean(state?.ready && state?.dataHydrated && state?.phase === 'ready');
}

function profileCardStyle(active, isLight) {
  if (isLight) {
    return {
      borderRadius: 26,
      border: `1px solid ${active ? 'rgba(14,165,233,.42)' : 'rgba(14,116,144,.16)'}`,
      background: active
        ? 'linear-gradient(180deg, rgba(226,247,255,.98), rgba(241,250,255,.96))'
        : 'rgba(255,255,255,.90)',
      padding: 18,
      cursor: 'pointer',
      boxShadow: active ? '0 0 28px rgba(14,165,233,.14), 0 10px 24px rgba(15,23,42,.06)' : '0 10px 22px rgba(15,23,42,.04)',
    };
  }
  return {
    borderRadius: 26,
    border: `1px solid ${active ? 'rgba(34,211,238,.45)' : 'rgba(103,232,249,.14)'}`,
    background: active
      ? 'linear-gradient(180deg, rgba(4,34,58,.9), rgba(2,15,30,.96))'
      : 'rgba(3,16,32,.82)',
    padding: 18,
    cursor: 'pointer',
    boxShadow: active ? '0 0 32px rgba(34,211,238,.16)' : 'none',
  };
}

export default function MorpheusProfileGate({ children }) {
  const location = useLocation();
  const { user, claims } = useAuth();
  const mcUserEmail = String(user?.email || claims?.email || '').trim().toLowerCase();
  const mcUserDisplayName = String(claims?.displayName || user?.displayName || '').trim();
  useEffect(() => { setMcUserContext(mcUserEmail); }, [mcUserEmail]);

  const [mcTheme, setMcTheme] = useState(() => getStoredMcTheme());
  const isLight = mcTheme === 'light';

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    // Theme/storage events can be emitted synchronously by MC helpers while a
    // route (notably Archives) is still rendering. Never let those events call
    // this gate's React setter in that render stack. Coalesce them and apply
    // the theme on the next macrotask instead.
    let themeTimer = null;
    let disposed = false;

    const commitStoredTheme = () => {
      if (disposed) return;
      const nextTheme = getStoredMcTheme();
      const nextIsLight = nextTheme === 'light';
      setMcTheme((current) => (current === nextTheme ? current : nextTheme));
      document.documentElement.classList.toggle('mc-theme-light', nextIsLight);
      document.body.classList.toggle('mc-theme-light', nextIsLight);
      document.documentElement.setAttribute('data-mc-theme', nextTheme);
      document.body.setAttribute('data-mc-theme', nextTheme);
    };

    const scheduleStoredTheme = () => {
      if (disposed || themeTimer !== null) return;
      themeTimer = window.setTimeout(() => {
        themeTimer = null;
        commitStoredTheme();
      }, 0);
    };

    // This effect itself already runs after commit, so the initial application
    // is safe. Event-driven updates are always deferred by scheduleStoredTheme.
    commitStoredTheme();
    window.addEventListener('storage', scheduleStoredTheme);
    window.addEventListener('mc-theme-change', scheduleStoredTheme);
    return () => {
      disposed = true;
      if (themeTimer !== null) window.clearTimeout(themeTimer);
      window.removeEventListener('storage', scheduleStoredTheme);
      window.removeEventListener('mc-theme-change', scheduleStoredTheme);
    };
  }, []);

  const [mcCloudReady, setMcCloudReady] = useState(() => isSafeMcSyncReady());
  const [syncState, setSyncState] = useState(() => getMcCloudSyncState());
  const [readyEventSeen, setReadyEventSeen] = useState(() => isSafeMcSyncReady());
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState('mc-admin');
  const [pin, setPin] = useState('');
  const [unlocked, setUnlocked] = useState(() => Boolean(getCurrentProfile()));
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [forceSyncing, setForceSyncing] = useState(false);
  const [routeRemountTick, setRouteRemountTick] = useState(0);

  useEffect(() => {
    let remountTimer = null;
    let disposed = false;

    // MC custom events can be emitted synchronously by route helpers. Deferring
    // every event-driven gate update keeps this parent gate from setting React
    // state while a child route is still rendering.
    const forceRouteRemount = () => {
      if (disposed || remountTimer !== null) return;
      remountTimer = window.setTimeout(() => {
        remountTimer = null;
        if (!disposed) setRouteRemountTick((value) => value + 1);
      }, 0);
    };

    window.addEventListener('mc-force-route-remount', forceRouteRemount);
    return () => {
      disposed = true;
      if (remountTimer !== null) window.clearTimeout(remountTimer);
      window.removeEventListener('mc-force-route-remount', forceRouteRemount);
    };
  }, []);

  useEffect(() => {
    const initialState = getMcCloudSyncState();
    const initialReady = isSafeMcSyncReady(initialState);
    setReadyEventSeen(initialReady);
    setMcCloudReady(initialReady);
    setSyncState(initialState);

    let cloudReadyTimer = null;
    let pendingCloudState = null;
    let disposed = false;

    function applyCloudReadyState(state) {
      if (disposed) return;
      const safeReady = isSafeMcSyncReady(state);
      setSyncState(state);
      setMcCloudReady(safeReady);
      if (safeReady) setReadyEventSeen(true);
      if (!safeReady) {
        // Firestore can briefly report a hydrating / force-syncing state while the
        // user is already inside MC. Do not kick them back to the Access Layer for
        // that transient sync pulse; keep the tab unlock and current profile intact.
        if (!getCurrentProfile()) {
          setProfiles([]);
          setUnlocked(false);
          setPin('');
        } else {
          setAccessUnlockedForThisTab(true);
          setUnlocked(true);
        }
      }
    }

    function syncCloudReady(event) {
      pendingCloudState = event?.detail || getMcCloudSyncState();
      if (disposed || cloudReadyTimer !== null) return;
      cloudReadyTimer = window.setTimeout(() => {
        cloudReadyTimer = null;
        const state = pendingCloudState || getMcCloudSyncState();
        pendingCloudState = null;
        applyCloudReadyState(state);
      }, 0);
    }

    window.addEventListener(MC_SYNC_READY_EVENT, syncCloudReady);
    return () => {
      disposed = true;
      if (cloudReadyTimer !== null) window.clearTimeout(cloudReadyTimer);
      window.removeEventListener(MC_SYNC_READY_EVENT, syncCloudReady);
    };
  }, [mcUserEmail]);

  useEffect(() => {
    if (!readyEventSeen || !mcCloudReady) {
      // Keep an already-unlocked tab open during temporary cloud sync refreshes.
      // The profile is local-only for this tab and should not be cleared just
      // because Firestore is rehydrating after a manual recovery/write.
      if (getCurrentProfile()) {
        setAccessUnlockedForThisTab(true);
        setUnlocked(true);
        return;
      }
      setProfiles([]);
      setUnlocked(false);
      setPin('');
      setSearch('');
      return;
    }
    const next = ensureAdminProfile(mcUserEmail, mcUserDisplayName);
    setProfiles(next);
    const current = getCurrentProfile();
    const currentEmail = String(current?.email || current?.userEmail || '').trim().toLowerCase();
    const isAdmin = isAdminProfile(current);
    if (!current || (isAdmin && mcUserEmail && currentEmail && currentEmail !== mcUserEmail)) {
      clearCurrentProfile();
      setAccessUnlockedForThisTab(false);
      setUnlocked(false);
      setPin('');
      setSearch('');
    }
    const ownAdmin = next.find((item) => isAdminProfile(item) && String(item.email || item.userEmail || '').trim().toLowerCase() === mcUserEmail) || next.find((item) => isAdminProfile(item)) || next[0];
    if (!current || (isAdmin && mcUserEmail && currentEmail !== mcUserEmail)) {
      setSelectedId(ownAdmin?.id || 'mc-admin');
    }
  }, [mcUserEmail, mcUserDisplayName, mcCloudReady, readyEventSeen]);

  useEffect(() => {
    let switchUserTimer = null;
    let disposed = false;

    const applySwitchUser = () => {
      if (disposed) return;
      clearCurrentProfile();
      setAccessUnlockedForThisTab(false);
      setUnlocked(false);
      setPin('');
      setSearch('');
      setError('');
      const state = getMcCloudSyncState();
      const safeReady = isSafeMcSyncReady(state);
      setSyncState(state);
      setMcCloudReady(safeReady);
      if (safeReady) {
        setReadyEventSeen(true);
        const next = ensureAdminProfile(mcUserEmail, mcUserDisplayName);
        setProfiles(next);
        setSelectedId((currentId) => (
          next.some((item) => item.id === currentId)
            ? currentId
            : (next.find((item) => isAdminProfile(item) && String(item.email || item.userEmail || '').trim().toLowerCase() === mcUserEmail)?.id || next.find((item) => isAdminProfile(item))?.id || next[0]?.id || 'mc-admin')
        ));
      }
    };

    const handleSwitchUser = () => {
      if (disposed || switchUserTimer !== null) return;
      switchUserTimer = window.setTimeout(() => {
        switchUserTimer = null;
        applySwitchUser();
      }, 0);
    };

    window.addEventListener('mc-profile-switch-user', handleSwitchUser);
    return () => {
      disposed = true;
      if (switchUserTimer !== null) window.clearTimeout(switchUserTimer);
      window.removeEventListener('mc-profile-switch-user', handleSwitchUser);
    };
  }, [mcUserEmail, mcUserDisplayName]);

  useEffect(() => {
    let profileSyncTimer = null;
    let disposed = false;

    const applyProfiles = () => {
      if (disposed) return;
      if (!readyEventSeen || !mcCloudReady || !isMcCloudSyncReady()) {
        setProfiles((prev) => (prev.length ? [] : prev));
        return;
      }
      const next = ensureAdminProfile(mcUserEmail, mcUserDisplayName);
      setProfiles((prev) => {
        try {
          if (JSON.stringify(prev || []) === JSON.stringify(next || [])) return prev;
        } catch {}
        return next;
      });
      setSelectedId((currentId) => (
        next.some((item) => item.id === currentId)
          ? currentId
          : (next.find((item) => isAdminProfile(item) && String(item.email || item.userEmail || '').trim().toLowerCase() === mcUserEmail)?.id || next.find((item) => isAdminProfile(item))?.id || next[0]?.id || 'mc-admin')
      ));
    };

    // Both native storage events and MC's cloud-ready event may be dispatched
    // from helpers called by route render code. Never mutate ProfileGate state
    // in the emitter's stack; coalesce bursts and apply after render completes.
    const syncProfiles = () => {
      if (disposed || profileSyncTimer !== null) return;
      profileSyncTimer = window.setTimeout(() => {
        profileSyncTimer = null;
        applyProfiles();
      }, 0);
    };

    window.addEventListener('storage', syncProfiles);
    window.addEventListener('mc-cloud-sync-ready', syncProfiles);
    return () => {
      disposed = true;
      if (profileSyncTimer !== null) window.clearTimeout(profileSyncTimer);
      window.removeEventListener('storage', syncProfiles);
      window.removeEventListener('mc-cloud-sync-ready', syncProfiles);
    };
  }, [mcUserEmail, mcUserDisplayName, mcCloudReady, readyEventSeen]);

  const visibleProfiles = useMemo(() => {
    const hiddenEmails = new Set(['info@invoicemate.co.za']);
    const available = profiles.filter((item) => {
      const email = String(item?.email || item?.userEmail || '').trim().toLowerCase();
      return !hiddenEmails.has(email);
    });
    const term = search.trim().toLowerCase();
    if (!term) return available;
    return available.filter((item) => {
      const profileName = buildProfileDisplayName(item).toLowerCase();
      const siteNames = formatProfileAllowedSites(item, { max: 20 }).toLowerCase();
      const email = String(item?.email || item?.userEmail || '').toLowerCase();
      return profileName.includes(term) || siteNames.includes(term) || email.includes(term);
    });
  }, [profiles, search]);

  const selected = useMemo(
    () => (mcCloudReady ? (visibleProfiles.find((item) => item.id === selectedId) || visibleProfiles[0] || null) : null),
    [visibleProfiles, selectedId, mcCloudReady]
  );

  const syncReadyForEntry = Boolean(
    readyEventSeen
    && mcCloudReady
    && syncState?.dataHydrated
    && syncState?.phase === 'ready'
  );
  const cloudProfilesHydrated = Boolean(syncState?.hasProfilesDoc || syncState?.profilesHydrated);
  const hasOnlyFallbackAdmin = profiles.length === 1
    && isAdminProfile(profiles[0])
    && String(profiles[0]?.pin || '') === '000000'
    && !cloudProfilesHydrated;
  const hasTrustedCachedProfiles = Boolean(
    profiles.length > 0
    && !hasOnlyFallbackAdmin
    && (profiles.length > 1 || cloudProfilesHydrated || profiles.some((profile) => String(profile?.pin || '') !== '000000'))
  );

  const profileListIsHydrated = Boolean(
    profiles.length > 0
    && !hasOnlyFallbackAdmin
    && (
      (syncReadyForEntry && cloudProfilesHydrated)
      // Warm refresh path: if this browser already has a real profile list from
      // the same org, do not force the user through another long sync screen.
      // A fallback-only admin list is still blocked above, so 000000 cannot open
      // MC before the real profiles exist.
      || (syncState?.warmCache && hasTrustedCachedProfiles)
    )
  );

  useEffect(() => {
    const current = getCurrentProfile();
    if (!profileListIsHydrated) {
      // The selected Access Layer identity is tab-local authority. Background
      // Firestore hydration must not replace it with the Firebase account.
      if (current) {
        setAccessUnlockedForThisTab(true);
        setUnlocked(true);
        return;
      }
      setUnlocked(false);
      setPin('');
      setSearch('');
      return;
    }
    if (!current) {
      clearCurrentProfile();
      setAccessUnlockedForThisTab(false);
      setUnlocked(false);
      setPin('');
      setSearch('');
      return;
    }
    setAccessUnlockedForThisTab(true);
    setUnlocked(true);
  }, [mcUserEmail, mcUserDisplayName, mcCloudReady, profileListIsHydrated, readyEventSeen]);

  function unlock() {
    if (!profileListIsHydrated) {
      setError('Morpheus profiles are still syncing. Please wait a few seconds and try again.');
      return;
    }
    if (!selected) return;
    if (!/^\d{5,6}$/.test(String(pin || ''))) {
      setError('PIN must be 5 or 6 digits.');
      return;
    }
    if (String(pin) !== String(selected.pin || '')) {
      setError('Incorrect PIN');
      return;
    }
    setCurrentProfile(selected);
    recordActivity({category:'Access',action:'Logged in',title:`Morpheus accessed · ${[selected?.name, selected?.surname].filter(Boolean).join(' ') || 'Profile'}`,personId:selected?.id,person:[selected?.name, selected?.surname].filter(Boolean).join(' ') || selected?.email || 'Profile',role:selected?.operationalRole || selected?.accessLevel || selected?.role || '',site:Array.isArray(selected?.allowedSiteIds) && selected.allowedSiteIds.includes('ALL') ? 'All Sites' : (selected?.storeName || selected?.store || 'Assigned sites'),metadata:{accessLevel:selected?.accessLevel || selected?.role}});
    setAccessUnlockedForThisTab(true);
    setUnlocked(true);
    setPin('');
    setError('');
    setSearch('');
  }

  async function handleForceSync() {
    setForceSyncing(true);
    setError('');
    try {
      const result = await forceMcCloudSync();
      const state = getMcCloudSyncState();
      setSyncState(state);
      setMcCloudReady(isSafeMcSyncReady(state));
      if (result?.ok) {
        setReadyEventSeen(true);
      }
      if (!result?.ok) {
        setError('Database is still syncing. Please wait a few seconds, then press Sync Database again.');
      } else {
        setError('');
      }
    } catch (err) {
      setError(`Sync failed: ${String(err?.message || err)}`);
    } finally {
      setForceSyncing(false);
    }
  }

  async function handleLogout() {
    recordActivity({category:'Access',action:'Logged out',title:'Morpheus logout',description:'The active profile signed out.'});
    try {
      clearCurrentProfile();
      setAccessUnlockedForThisTab(false);
      await signOut(getAuthClient());
    } finally {
      window.location.assign('/login');
    }
  }

  if (unlocked) {
    const profileKey = `${getCurrentProfile()?.id || 'mc-open'}:${location.pathname}:${location.search}:${location.key || ''}:${routeRemountTick}`;
    return <div key={profileKey}>{children}</div>;
  }

  const colors = isLight ? {
    overlay: 'rgba(15,32,48,.38)',
    shellBorder: 'rgba(14,116,144,.18)',
    shellBg: 'radial-gradient(circle at 18% 10%, rgba(56,189,248,.14), transparent 26%), linear-gradient(180deg, rgba(255,255,255,.98), rgba(241,249,255,.96))',
    shellShadow: '0 40px 120px rgba(15,23,42,.20), inset 0 0 0 1px rgba(255,255,255,.78)',
    grid: 'linear-gradient(rgba(14,165,233,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,.06) 1px, transparent 1px)',
    text: '#0a2540',
    muted: '#31516d',
    eyebrow: '#0369a1',
    panelBg: 'linear-gradient(180deg, rgba(255,255,255,.94), rgba(245,251,255,.92))',
    panelBorder: 'rgba(14,116,144,.16)',
    inputBg: 'rgba(255,255,255,.96)',
    inputText: '#0f2a44',
    inputPlaceholder: '#6b8aa5',
    pillBg: 'rgba(255,255,255,.92)',
    pillText: '#075985',
    secondaryBg: 'rgba(255,255,255,.92)',
    secondaryText: '#0f2a44',
  } : {
    overlay: 'rgba(2,6,23,.62)',
    shellBorder: 'rgba(103,232,249,.14)',
    shellBg: 'radial-gradient(circle at top, rgba(12,74,110,.42), rgba(2,10,24,.97) 58%)',
    shellShadow: '0 40px 120px rgba(0,0,0,.55)',
    grid: 'linear-gradient(rgba(34,211,238,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,.05) 1px, transparent 1px)',
    text: '#f8fafc',
    muted: 'rgba(226,232,240,.78)',
    eyebrow: '#67e8f9',
    panelBg: 'rgba(2,12,28,.74)',
    panelBorder: 'rgba(103,232,249,.12)',
    inputBg: 'rgba(2,12,28,.9)',
    inputText: '#f8fafc',
    inputPlaceholder: '#8aa7bc',
    pillBg: 'transparent',
    pillText: '#a5f3fc',
    secondaryBg: 'rgba(255,255,255,.02)',
    secondaryText: '#e2e8f0',
  };

  return (
    <>
      {children}
      <div className="mcGate" style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '28px 24px', overflowY: 'auto' }}>
        <div className="mcGate__backdrop" style={{ position: 'absolute', inset: 0, background: colors.overlay, backdropFilter: 'blur(14px)' }} />

        <div className="mcGate__card" style={{ position: 'relative', width: 'min(1180px, 94vw)', maxHeight: 'calc(100vh - 56px)', borderRadius: 34, overflow: 'hidden', border: `1px solid ${colors.shellBorder}`, background: colors.shellBg, boxShadow: colors.shellShadow }}>
          <div className="mcGate__scan" style={{ position: 'absolute', inset: 0, backgroundImage: colors.grid, backgroundSize: '28px 28px', opacity: isLight ? 0.78 : 0.65 }} />

          <div className="mcGate__content" style={{ position: 'relative', padding: '42px 40px 34px', color: colors.text, maxHeight: 'calc(100vh - 56px)', overflowY: 'auto' }}>
            <div className="mcGate__eyebrow" style={{ fontSize: 12, letterSpacing: '.35em', textTransform: 'uppercase', color: colors.eyebrow }}>Morpheus Access Layer</div>

            <div style={{ marginTop: 14, fontSize: 'clamp(58px, 7vw, 88px)', lineHeight: 0.94, fontWeight: 300, color: colors.text }}>Enter Morpheus</div>

            <div className="mcGate__subtitle" style={{ marginTop: 18, fontSize: 16, color: colors.muted }}>Select your identity, confirm your PIN, and drop into your counting world.</div>

            <div className="mcGate__accessGrid" style={{ marginTop: 34, display: 'grid', gridTemplateColumns: '1.1fr .95fr', gap: 28 }}>
              <div className="mcGate__left" style={{ borderRadius: 28, border: `1px solid ${colors.panelBorder}`, background: colors.panelBg, padding: 28, minHeight: 460, maxHeight: '52vh', overflowY: 'auto', boxShadow: isLight ? '0 18px 44px rgba(15,23,42,.08)' : 'none' }}>
                <div className="mcGate__label" style={{ fontSize: 12, letterSpacing: '.28em', color: colors.eyebrow, textTransform: 'uppercase' }}>Search Name</div>

                <input autoComplete="off" data-lpignore="true" data-1p-ignore="true" spellCheck={false} name="mc_access_search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search profile..." style={{ width: '100%', marginTop: 20, borderRadius: 22, border: `1px solid ${isLight ? 'rgba(14,116,144,.20)' : 'rgba(103,232,249,.14)'}`, background: colors.inputBg, color: colors.inputText, fontSize: 20, padding: '18px 22px', outline: 'none' }} />

                <div className="mcGate__profileList" style={{ marginTop: 18, display: 'grid', gap: 14, maxHeight: '430px', overflowY: 'auto', overscrollBehavior: 'contain', paddingRight: 4 }}>
                  {!profileListIsHydrated ? (
                    <div style={{ borderRadius: 24, border: `1px solid ${isLight ? 'rgba(14,116,144,.18)' : 'rgba(103,232,249,.16)'}`, background: isLight ? 'rgba(255,255,255,.88)' : 'rgba(2,12,28,.72)', padding: 22, color: colors.muted, lineHeight: 1.6 }}>
                      <div style={{ color: colors.eyebrow, fontWeight: 900, letterSpacing: '.18em', textTransform: 'uppercase', fontSize: 12 }}>Database syncing</div>
                      <div style={{ marginTop: 10, fontWeight: 700 }}>Please wait while Morpheus loads the org profiles and count data from Firestore.</div>
                      <div style={{ marginTop: 8, fontSize: 13, opacity: 0.78 }}>{syncReadyForEntry && !cloudProfilesHydrated ? 'Waiting for the real org profiles document to finish syncing…' : (syncState?.phase === 'waiting-for-profiles' ? 'Waiting for the profiles document to finish syncing…' : 'Hydrating organisation profiles…')}</div>
                    </div>
                  ) : visibleProfiles.map((profile) => {
                    const active = profile.id === selected?.id;
                    return (
                      <button key={profile.id} type="button" onClick={() => setSelectedId(profile.id)} style={profileCardStyle(active, isLight)}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: colors.text, textAlign: 'left' }}>{buildProfileDisplayName(profile)}</div>
                        <div style={{ marginTop: 10, color: isLight ? '#31516d' : 'rgba(186,230,253,.9)', textAlign: 'left' }}>{formatProfileAllowedSites(profile)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mcGate__profileCard" style={{ borderRadius: 28, border: `1px solid ${colors.panelBorder}`, background: colors.panelBg, padding: 28, boxShadow: isLight ? '0 18px 44px rgba(15,23,42,.08)' : 'none' }}>
                <div className="mcGate__label" style={{ fontSize: 12, letterSpacing: '.28em', color: colors.eyebrow, textTransform: 'uppercase' }}>Selected Identity</div>

                <div style={{ marginTop: 26, fontSize: 42, fontWeight: 300, color: colors.text }}>{profileListIsHydrated ? (selected ? buildProfileDisplayName(selected) : 'Select profile') : 'Syncing…'}</div>

                <div className="mcGate__storePill" style={{ display: 'inline-flex', marginTop: 18, padding: '14px 18px', borderRadius: 999, border: `1px solid ${isLight ? 'rgba(14,116,144,.18)' : 'rgba(103,232,249,.16)'}`, background: colors.pillBg, color: colors.pillText, fontWeight: 700 }}>
                  {!profileListIsHydrated ? 'Waiting for database' : (isAdminProfile(selected) ? 'All Sites' : formatProfileAllowedSites(selected, { max: 20 }))}
                </div>

                <div className="mcGate__label" style={{ marginTop: 26, fontSize: 12, letterSpacing: '.28em', color: colors.eyebrow, textTransform: 'uppercase' }}>PIN</div>

                <input autoComplete="new-password" inputMode="numeric" pattern="[0-9]*" enterKeyHint="done" data-lpignore="true" data-1p-ignore="true" data-form-type="other" type="password" name={`mc_access_pin_${mcUserEmail.replace(/[^a-z0-9]/g, '_')}`} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} disabled={!profileListIsHydrated} onKeyDown={(e) => e.key === 'Enter' && unlock()} minLength={5} maxLength={6} placeholder={profileListIsHydrated ? 'Enter 5 or 6-digit PIN' : 'Syncing database'} style={{ width: '100%', marginTop: 16, borderRadius: 24, border: `1px solid ${isLight ? 'rgba(14,116,144,.22)' : 'rgba(34,211,238,.34)'}`, background: colors.inputBg, color: colors.inputText, fontSize: 22, letterSpacing: '.28em', padding: '22px 24px', outline: 'none', boxShadow: isLight ? 'inset 0 0 0 1px rgba(255,255,255,.75)' : '0 0 0 3px rgba(34,211,238,.08)' }} />

                {!profileListIsHydrated ? <div style={{ marginTop: 16, color: isLight ? '#0369a1' : '#7dd3fc', fontWeight: 800 }}>Syncing Morpheus profiles and count data from the org database…</div> : null}
                {error ? <div className="mcGate__error" style={{ marginTop: 16, color: isLight ? '#be123c' : '#fca5a5', fontWeight: 700 }}>{error}</div> : null}

                <div className="mcGate__actions" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 28 }}>
                  <button type="button" onClick={unlock} disabled={!profileListIsHydrated} style={{ borderRadius: 999, border: 0, background: 'linear-gradient(135deg, #67e8f9 0%, #22c7f6 100%)', color: '#06202f', fontWeight: 900, fontSize: 18, padding: '18px 28px', cursor: profileListIsHydrated ? 'pointer' : 'not-allowed', opacity: profileListIsHydrated ? 1 : 0.55, boxShadow: '0 14px 30px rgba(14,165,233,.22)' }}>{profileListIsHydrated ? 'Access Dashboard' : 'Database syncing…'}</button>

                  <button type="button" onClick={() => { clearCurrentProfile(); setAccessUnlockedForThisTab(false); setSelectedId('mc-admin'); setPin(''); setSearch(''); }} style={{ borderRadius: 999, border: `1px solid ${isLight ? 'rgba(14,116,144,.18)' : 'rgba(255,255,255,.12)'}`, background: colors.secondaryBg, color: colors.secondaryText, fontWeight: 700, fontSize: 16, padding: '18px 22px', cursor: 'pointer' }}>Reset</button>

                  <button type="button" onClick={handleForceSync} disabled={forceSyncing} style={{ borderRadius: 999, border: `1px solid ${isLight ? 'rgba(14,116,144,.16)' : 'rgba(255,255,255,.10)'}`, background: isLight ? 'rgba(226,232,240,.72)' : 'rgba(148,163,184,.10)', color: isLight ? '#334155' : '#cbd5e1', fontWeight: 700, fontSize: 16, padding: '18px 22px', cursor: forceSyncing ? 'not-allowed' : 'pointer', opacity: forceSyncing ? 0.7 : 1 }}>{forceSyncing ? 'Syncing…' : 'Sync Database'}</button>

                  <button type="button" onClick={handleLogout} style={{ borderRadius: 999, border: `1px solid ${isLight ? 'rgba(14,116,144,.18)' : 'rgba(255,255,255,.12)'}`, background: colors.secondaryBg, color: colors.secondaryText, fontWeight: 700, fontSize: 16, padding: '18px 22px', cursor: 'pointer' }}>Logout</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
